import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { paymentsAPI } from '../../lib/api';
import { PublicLayout } from '../../components/PublicLayout';
import { CheckCircle, Clock, XCircle, Loader2, Home, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function BookingConfirmation() {
  const { reference } = useParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);
  const [pollCount, setPollCount] = useState(0);

  const checkStatus = useCallback(async () => {
    try {
      const res = await paymentsAPI.checkStatus(reference);
      setStatus(res.data);
      
      // Stop polling once we have a final status
      const ps = res.data.payment_status;
      if (['paid', 'pay_at_property', 'cancelled', 'failed', 'expired'].includes(ps)) {
        setPolling(false);
      }
    } catch {
      setStatus({ payment_status: 'unknown', booking_status: 'unknown' });
    } finally {
      setLoading(false);
    }
  }, [reference]);

  useEffect(() => {
    // Initial check
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (!polling || pollCount > 60) {
      setPolling(false);
      return;
    }
    
    // Poll every 3 seconds for the first 30 checks, then every 10 seconds
    const interval = pollCount < 30 ? 3000 : 10000;
    const timer = setTimeout(() => {
      checkStatus();
      setPollCount((c) => c + 1);
    }, interval);
    
    return () => clearTimeout(timer);
  }, [polling, pollCount, checkStatus]);

  const manualRefresh = () => {
    setLoading(true);
    checkStatus();
  };

  const getStatusDisplay = () => {
    if (!status) return null;
    const ps = status.payment_status;

    if (ps === 'paid') {
      return {
        icon: <CheckCircle className="h-16 w-16 text-green-500" />,
        title: 'Payment Successful!',
        description: 'Your payment has been processed and your booking is confirmed.',
        color: 'text-green-600',
        bg: 'bg-green-50',
      };
    }
    if (ps === 'pay_at_property') {
      return {
        icon: <CheckCircle className="h-16 w-16 text-blue-500" />,
        title: 'Booking Confirmed!',
        description: 'Your booking is confirmed. Please pay at the property upon arrival.',
        color: 'text-blue-600',
        bg: 'bg-blue-50',
      };
    }
    if (ps === 'pending') {
      return {
        icon: <Loader2 className="h-16 w-16 text-amber-500 animate-spin" />,
        title: 'Processing Payment...',
        description: 'We\'re verifying your payment with Mollie. This page will update automatically.',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
      };
    }
    if (ps === 'payment_error') {
      return {
        icon: <Clock className="h-16 w-16 text-amber-500" />,
        title: 'Payment Processing',
        description: 'There was a temporary issue. We\'re checking with the payment provider...',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
      };
    }
    if (['cancelled', 'failed', 'expired'].includes(ps)) {
      return {
        icon: <XCircle className="h-16 w-16 text-red-500" />,
        title: ps === 'cancelled' ? 'Payment Cancelled' : 'Payment Failed',
        description: ps === 'expired' 
          ? 'Your payment session has expired. Please try booking again.'
          : 'Your payment could not be processed. Please try again.',
        color: 'text-red-600',
        bg: 'bg-red-50',
      };
    }
    return {
      icon: <Loader2 className="h-16 w-16 text-gray-400 animate-spin" />,
      title: 'Checking Payment...',
      description: 'We\'re checking your booking status with the payment provider.',
      color: 'text-gray-600',
      bg: 'bg-gray-50',
    };
  };

  const display = getStatusDisplay();

  return (
    <PublicLayout>
      <div className="min-h-[60vh] flex items-center justify-center py-12" data-testid="booking-confirmation-page">
        <div className="max-w-lg w-full mx-auto text-center px-6">
          {loading && !status ? (
            <div className="space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="text-muted-foreground">Loading booking status...</p>
            </div>
          ) : (
            <div className={`rounded-2xl p-8 ${display?.bg || 'bg-gray-50'}`}>
              <div className="flex justify-center mb-6">{display?.icon}</div>
              <h1 className={`text-2xl font-semibold mb-3 ${display?.color}`} data-testid="confirmation-title">
                {display?.title}
              </h1>
              <p className="text-muted-foreground mb-6">{display?.description}</p>
              
              <div className="bg-white rounded-xl p-5 mb-6 shadow-sm">
                <p className="text-sm text-muted-foreground mb-1">Booking Reference</p>
                <p className="text-2xl font-mono font-bold tracking-wider" data-testid="booking-ref">
                  {reference}
                </p>
                {status?.mollie_status && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Payment provider status: <span className="font-medium">{status.mollie_status}</span>
                  </p>
                )}
              </div>

              {polling && (
                <p className="text-xs text-muted-foreground mb-4 flex items-center justify-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Auto-refreshing...
                </p>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {!polling && status?.payment_status === 'pending' && (
                  <Button onClick={manualRefresh} variant="outline" data-testid="refresh-status-btn">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Check Again
                  </Button>
                )}
                <Link to="/">
                  <Button variant="outline" data-testid="back-home-btn">
                    <Home className="h-4 w-4 mr-2" />
                    Back to Home
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
